import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/UI/Card';
import StatCard from '../components/UI/StatCard';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import Table from '../components/UI/Table';
import { positionsAPI, tradesAPI, strategiesAPI, accountAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PageTransition, StaggerContainer, AnimatedItem, Spinner } from '@/components/motion';
import { useMediaQuery } from '../hooks/useMediaQuery';

/** Format a number with commas and fixed decimals */
const fmt = (n, decimals = 2) =>
  parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** Format a dollar value with sign and color class */
const fmtPnL = (n) => {
  const v = parseFloat(n || 0);
  const sign = v >= 0 ? '+' : '-';
  return { text: `${sign}$${fmt(Math.abs(v))}`, color: v >= 0 ? 'text-green-500' : 'text-red-500' };
};

function Portfolio() {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const chartHeight = isMobile ? 220 : 300;
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({
    totalPositions: 0,
    totalExposure: 0,
    unrealizedPnL: 0,
    realizedPnL: 0,
    totalPnL: 0,
  });
  const [selectedView, setSelectedView] = useState('open');

  useEffect(() => {
    loadPortfolioData();
    const interval = setInterval(loadPortfolioData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPortfolioData = async () => {
    try {
      const [allPositions, allTrades, allStrategies, accountSummary] = await Promise.all([
        positionsAPI.getAll().catch(() => ({ data: [] })),
        tradesAPI.getAll().catch(() => ({ data: [] })),
        strategiesAPI.getAll().catch(() => ({ data: [] })),
        accountAPI.getSummary().catch(() => ({ data: [] })),
      ]);

      const openPositions = (allPositions.data || []).filter((p) => p.status === 'open');
      const closed = (allPositions.data || []).filter((p) => p.status === 'closed');

      setPositions(openPositions);
      setClosedPositions(closed);
      setStrategies(allStrategies.data || []);
      setAccounts(accountSummary.data || []);

      const totalExposure = openPositions.reduce(
        (sum, pos) => sum + parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price), 0
      );
      const unrealizedPnL = openPositions.reduce(
        (sum, pos) => sum + parseFloat(pos.unrealized_pnl || 0), 0
      );
      const realizedPnL = openPositions.reduce(
        (sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0
      ) + closed.reduce(
        (sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0
      );

      setStats({
        totalPositions: openPositions.length,
        totalExposure,
        unrealizedPnL,
        realizedPnL,
        totalPnL: unrealizedPnL + realizedPnL,
      });
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group accounts by environment
  const paperAccounts = accounts.filter((a) => a.environment === 'paper' && !a.error);
  const liveAccounts = accounts.filter((a) => a.environment === 'live' && !a.error);

  const sumField = (accts, field) => accts.reduce((s, a) => s + (a[field] || 0), 0);

  const positionColumns = [
    {
      key: 'symbol',
      label: 'Symbol',
      render: (symbol, row) => (
        <div>
          <p className="font-semibold text-foreground">{symbol}</p>
          <p className="text-xs text-muted-foreground/70">
            {strategies.find((s) => s.id === row.strategy_id)?.name || 'Unknown Strategy'}
          </p>
        </div>
      ),
    },
    {
      key: 'quantity',
      label: 'Side / Qty',
      render: (quantity) => {
        const qty = parseFloat(quantity);
        const isLong = qty >= 0;
        return (
          <div className="flex items-center gap-2">
            <Badge variant={isLong ? 'success' : 'danger'}>{isLong ? 'LONG' : 'SHORT'}</Badge>
            <span>{Math.abs(qty).toFixed(2)}</span>
          </div>
        );
      },
    },
    {
      key: 'entry_price',
      label: 'Entry Price',
      render: (price) => `$${fmt(price)}`,
    },
    {
      key: 'current_price',
      label: 'Current Price',
      render: (price, row) => `$${fmt(price || row.entry_price)}`,
    },
    {
      key: 'unrealized_pnl',
      label: 'Unrealized P&L',
      render: (pnl, row) => {
        const { text, color } = fmtPnL(pnl);
        const cost = parseFloat(row.quantity) * parseFloat(row.entry_price);
        const pct = cost !== 0 ? ((parseFloat(pnl || 0) / cost) * 100).toFixed(2) : '0.00';
        return (
          <div className={color}>
            <p className="font-semibold">{text}</p>
            <p className="text-xs">{pct}%</p>
          </div>
        );
      },
    },
    {
      key: 'exposure',
      label: 'Exposure',
      render: (_, row) => {
        const exposure = parseFloat(row.quantity) * parseFloat(row.current_price || row.entry_price);
        return `$${fmt(exposure)}`;
      },
    },
  ];

  const closedPositionColumns = [
    {
      key: 'symbol',
      label: 'Symbol',
      render: (symbol, row) => (
        <div>
          <p className="font-semibold text-foreground">{symbol}</p>
          <p className="text-xs text-muted-foreground/70">
            {strategies.find((s) => s.id === row.strategy_id)?.name || 'Unknown Strategy'}
          </p>
        </div>
      ),
    },
    { key: 'quantity', label: 'Quantity', render: (q) => parseFloat(q).toFixed(2) },
    { key: 'entry_price', label: 'Entry Price', render: (p) => `$${fmt(p)}` },
    { key: 'current_price', label: 'Exit Price', render: (p, row) => `$${fmt(p || row.entry_price || 0)}` },
    {
      key: 'realized_pnl',
      label: 'Realized P&L',
      render: (pnl) => {
        const { text, color } = fmtPnL(pnl);
        return <span className={`font-semibold ${color}`}>{text}</span>;
      },
    },
    { key: 'closed_at', label: 'Closed At', render: (d) => (d ? new Date(d).toLocaleString() : '-') },
  ];

  // Chart data
  const exposureBySymbol = positions.reduce((acc, pos) => {
    const exposure = parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price);
    const existing = acc.find((item) => item.symbol === pos.symbol);
    if (existing) existing.value += exposure;
    else acc.push({ symbol: pos.symbol, value: exposure });
    return acc;
  }, []);

  const exposureByStrategy = positions.reduce((acc, pos) => {
    const strategy = strategies.find((s) => s.id === pos.strategy_id);
    const name = strategy?.name || 'Unknown';
    const exposure = parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price);
    const existing = acc.find((item) => item.name === name);
    if (existing) existing.value += exposure;
    else acc.push({ name, value: exposure });
    return acc;
  }, []);

  const pnlBySymbol = positions.reduce((acc, pos) => {
    const pnl = parseFloat(pos.unrealized_pnl || 0);
    const existing = acc.find((item) => item.symbol === pos.symbol);
    if (existing) existing.pnl += pnl;
    else acc.push({ symbol: pos.symbol, pnl });
    return acc;
  }, []);

  const COLORS = ['#22c55e', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-16 w-16 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  const AccountRow = ({ label, value, valueClass }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${valueClass || 'text-foreground'}`}>${fmt(value)}</span>
    </div>
  );

  const AccountCard = ({ isPaper, accts = [] }) => {
    const title = isPaper ? 'Paper Trading' : 'Live Trading';
    const badge = isPaper ? 'PAPER' : 'LIVE';
    const totalEquity = sumField(accts, 'equity');
    const totalCash = sumField(accts, 'cash');
    const totalBP = sumField(accts, 'buyingPower');
    const totalNMBP = sumField(accts, 'nonMarginableBuyingPower');
    const totalLong = sumField(accts, 'longMarketValue');
    const totalShort = sumField(accts, 'shortMarketValue');
    const avgLeverage = totalEquity > 0 ? (totalLong + Math.abs(totalShort)) / totalEquity : 0;

    return (
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <Badge variant={isPaper ? 'info' : 'warning'}>{badge}</Badge>
          <span className="text-xs text-muted-foreground ml-auto">
            {accts.length} connection{accts.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="divide-y divide-border/50">
          <AccountRow label="Equity" value={totalEquity} />
          <AccountRow label="Cash" value={totalCash} />
          <AccountRow label="Buying Power" value={totalBP} />
          <AccountRow label="Crypto Buying Power" value={totalNMBP} />
          <AccountRow label="Long Market Value" value={totalLong} />
          <AccountRow label="Short Market Value" value={Math.abs(totalShort)} />
          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs text-muted-foreground">Leverage</span>
            <span className={`text-sm font-semibold ${avgLeverage > 2 ? 'text-yellow-400' : 'text-foreground'}`}>
              {avgLeverage.toFixed(2)}x
            </span>
          </div>
        </div>

        {accts.length > 1 && (
          <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
            <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">By Connection</p>
            {accts.map((a) => (
              <div key={a.connectionId} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground truncate max-w-[60%]">{a.connectionName}</span>
                <span className="font-semibold text-foreground">${fmt(a.equity)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  return (
    <PageTransition className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      {/* Account Balances */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AccountCard isPaper accts={paperAccounts} />
        <AccountCard isPaper={false} accts={liveAccounts} />
      </div>

      {/* Statistics */}
      <StaggerContainer className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6">
        <AnimatedItem>
          <StatCard title="Total Positions" value={stats.totalPositions} subtitle="Open positions" icon="💼" />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard title="Total Exposure" value={`$${fmt(stats.totalExposure)}`} subtitle="Market value" icon="💰" />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard
            title="Unrealized P&L"
            value={`$${fmt(stats.unrealizedPnL)}`}
            change={fmtPnL(stats.unrealizedPnL).text}
            changeType={stats.unrealizedPnL >= 0 ? 'positive' : 'negative'}
            icon="📊"
          />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard
            title="Realized P&L"
            value={`$${fmt(stats.realizedPnL)}`}
            change={fmtPnL(stats.realizedPnL).text}
            changeType={stats.realizedPnL >= 0 ? 'positive' : 'negative'}
            icon="✅"
          />
        </AnimatedItem>
        <AnimatedItem>
          <StatCard
            title="Total P&L"
            value={`$${fmt(stats.totalPnL)}`}
            change={fmtPnL(stats.totalPnL).text}
            changeType={stats.totalPnL >= 0 ? 'positive' : 'negative'}
            icon="🎯"
          />
        </AnimatedItem>
      </StaggerContainer>

      {/* Charts */}
      {positions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Exposure by Symbol">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={exposureBySymbol} cx="50%" cy="50%" labelLine={false} label={isMobile ? false : ({ symbol, percent }) => `${symbol} ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {exposureBySymbol.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `$${fmt(v)}`} contentStyle={{ backgroundColor: '#18181b', border: '1px solid hsl(var(--border))' }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Unrealized P&L by Symbol">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={pnlBySymbol}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="symbol" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid hsl(var(--border))' }} formatter={(v) => [`$${fmt(v)}`, 'P&L']} />
                <Bar dataKey="pnl" fill="#22c55e">
                  {pnlBySymbol.map((entry, i) => <Cell key={i} fill={entry.pnl >= 0 ? '#10B981' : '#EF4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Exposure by Strategy">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={exposureByStrategy}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid hsl(var(--border))' }} formatter={(v) => [`$${fmt(v)}`, 'Exposure']} />
                <Bar dataKey="value" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Position Distribution">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie data={exposureByStrategy} cx="50%" cy="50%" labelLine={false} label={isMobile ? false : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {exposureByStrategy.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `$${fmt(v)}`} contentStyle={{ backgroundColor: '#18181b', border: '1px solid hsl(var(--border))' }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        <Button variant={selectedView === 'open' ? 'primary' : 'ghost'} onClick={() => setSelectedView('open')}>
          Open Positions ({positions.length})
        </Button>
        <Button variant={selectedView === 'closed' ? 'primary' : 'ghost'} onClick={() => setSelectedView('closed')}>
          Closed Positions ({closedPositions.length})
        </Button>
      </div>

      {/* Positions Table */}
      <Card
        title={selectedView === 'open' ? 'Open Positions' : 'Closed Positions'}
        subtitle={selectedView === 'open' ? 'Currently active positions' : 'Historical closed positions'}
      >
        {selectedView === 'open' ? (
          positions.length > 0 ? (
            <Table columns={positionColumns} data={positions} />
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No open positions</p>
              <Link to="/strategies/builder"><Button>Create a Strategy</Button></Link>
            </div>
          )
        ) : (
          <Table columns={closedPositionColumns} data={closedPositions} emptyMessage="No closed positions yet" />
        )}
      </Card>

      {/* Risk Summary */}
      {positions.length > 0 && (
        <Card title="Risk Summary">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Largest Position</p>
              <p className="text-xl font-bold text-foreground">
                {positions.reduce((max, pos) => {
                  const exposure = parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price);
                  return exposure > (max.exposure || 0) ? { symbol: pos.symbol, exposure } : max;
                }, {}).symbol || '-'}
              </p>
              <p className="text-sm text-muted-foreground/70">
                ${fmt(positions.reduce((max, pos) => {
                  const exposure = parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price);
                  return exposure > (max.exposure || 0) ? { exposure } : max;
                }, {}).exposure || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Best Performing</p>
              {(() => {
                const best = positions.reduce((max, pos) => {
                  const pnl = parseFloat(pos.unrealized_pnl || 0);
                  return pnl > (max.pnl || 0) ? { symbol: pos.symbol, pnl } : max;
                }, {});
                return (
                  <>
                    <p className="text-xl font-bold text-green-500">{best.symbol || '-'}</p>
                    <p className="text-sm text-green-400">+${fmt(best.pnl || 0)}</p>
                  </>
                );
              })()}
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Worst Performing</p>
              {(() => {
                const worst = positions.reduce((min, pos) => {
                  const pnl = parseFloat(pos.unrealized_pnl || 0);
                  return pnl < (min.pnl || 0) ? { symbol: pos.symbol, pnl } : min;
                }, {});
                return (
                  <>
                    <p className="text-xl font-bold text-red-500">{worst.symbol || '-'}</p>
                    <p className="text-sm text-red-400">${fmt(worst.pnl || 0)}</p>
                  </>
                );
              })()}
            </div>
          </div>
        </Card>
      )}
    </PageTransition>
  );
}

export default Portfolio;
